import { buildProduct } from '@hive/platform-url-schema'
import type { MouseEvent } from 'react'
import { memo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router'
import { useMediaQuery } from 'usehooks-ts'

import { useAuth } from '@features/auth'
import { DeleteProduct, useCartItemTotal } from '@features/cart'
import { useCartStore } from '@features/cart/model/useCartStore'
import ControlCart from '@features/cart/ui/control'
import ActionButtons from '@features/cart/ui/preview/action-buttons'
import { useErrorInfoModal } from '@features/modal'
import { useAddressStore } from '@features/shipping-methods'

import { SimilarProductsTrigger } from '@widgets/products-similar'

import { isAgeRestrictedProduct } from '@entities/Product/helpers'
import { PRODUCT_NO_PHOTO_PATH } from '@shared/contants/product'
import { Product, useDropCartItemMutation } from '@shared/generated'
import { useAgeVerification, useProductUnit } from '@shared/hooks'
import { useAnalytics } from '@shared/lib/analytics'
import { cn } from '@shared/lib/cn'
import ButtonDelay from '@shared/ui/button-delay'
import ImageWithBg from '@shared/ui/image-with-bg'
import { formatPrice } from '@shared/uttils'


import styles from './styles.module.scss'

export interface ProductCartCardProps {
  data: Product;
  amount: number;
  className?: string;
  outStockProduct?: boolean;
}

function ProductCart (props: ProductCartCardProps) {
  const { openErrorInfoModal } = useErrorInfoModal()
  const { data, className, amount, outStockProduct } = props
  const [ isDeleted, setIsDeleted ] = useState(false)
  const [ fetchDropCartItem ] = useDropCartItemMutation()
  const { removeProduct, currentShopId } = useCartStore()
  const { isAuthorized } = useAuth()
  const navigate = useNavigate()
  const { shopId } = useAddressStore()
  const { t: tc } = useTranslation('common')
  const { t: tca } = useTranslation('cart')
  const { t: te } = useTranslation('errors')
  const { isAdult, guardAgeRestrictedAction } = useAgeVerification()
  const cartItemTotalPrice = useCartItemTotal(data, amount)
  const cartItemTotalOldPrice = useCartItemTotal(data, amount, true)
  const isMobile = useMediaQuery('(max-width: 640px)')
  const { removeFromCart: trackRemoveFromCart } = useAnalytics()
  const productUrl = buildProduct(data.slug as string)
  const isAgeControlRequired = !isAdult && isAgeRestrictedProduct(data)

  const handleDelete = async () => {
    trackRemoveFromCart(data, amount)
    if (isAuthorized) {
      try {
        await fetchDropCartItem({
          variables: {
            shopId: shopId,
            productId: data.id,
          },
        })

        removeProduct(data.id)
      }
      catch (error) {
        console.error('Failed to delete item from cart:', error)
        setIsDeleted(false)
        openErrorInfoModal({
          description: te('drop_cart_item'),
        })
      }
    }
    else {
      removeProduct(data.id)
    }
  }

  const { unit } = useProductUnit({
    isWeightProduct: data.isWeightProduct as boolean,
    value: amount,
    price: cartItemTotalPrice as number,
    t: tc,
  })

  const handleLinkClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (outStockProduct) {
      e.preventDefault()
      return
    }

    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return
    }

    e.preventDefault()

    guardAgeRestrictedAction(isAgeControlRequired, () => {
      navigate(productUrl)
    })
  }

  return (
    <div
      className={cn(styles.root, className, {
        [styles.out]: outStockProduct,
      })}
    >
      <Link
        to={productUrl}
        className={styles.root__image}
        onClick={handleLinkClick}
      >
        <ImageWithBg
          className={styles['root__image-inner']}
          src={data.preview?.url || PRODUCT_NO_PHOTO_PATH}
        />
      </Link>

      <div className={styles.root__info}>
        <div className={styles.root__info_left}>
          <Link
            to={productUrl}
            className={styles.root__name}
            onClick={handleLinkClick}
          >
            {data.name}
          </Link>

          {/* Quantity controls for desktop */}
          {!isMobile && !outStockProduct && (
            <ControlCart
              classNameControl={styles.root__controlDesktop}
              isTablet={false}
              product={data}
              noTotals={true}
            />
          )}

        </div>

        <div className={styles.root__info_right}>

          {!outStockProduct && (
            <div className={styles['root__info-wrapper']}>
              <div className={styles.root__price}>
                {cartItemTotalPrice && (
                  <div className={styles['root__price-value']}>
                    {formatPrice(cartItemTotalPrice)}
                  </div>
                )}
                {!!data.priceOffer?.oldPrice && cartItemTotalOldPrice && (
                  <div className={styles['root__price-old']}>
                    {formatPrice(cartItemTotalOldPrice as number)}
                  </div>
                )}
              </div>

              <span className={styles.root__count}>
                {amount} {unit} x {formatPrice(data.priceOffer?.price as number)}
              </span>


              {!isMobile && (
                <div className={styles['root__cart-item__actions']}>
                  {isDeleted && !outStockProduct && (
                    <ButtonDelay
                      className={styles.root__control}
                      duration={3000}
                      onFinish={async () => {
                        await handleDelete()
                      }}
                      onClick={() => {
                        setIsDeleted(false)
                      }}
                    >
                      {tca('preview.cart_return')}
                    </ButtonDelay>
                  )}

                  {!isDeleted && <ActionButtons product={data} onRemove={() => setIsDeleted(true)} size='medium' buttonsTemplate={[ 'comment', 'favorite', 'delete' ]} />}
                </div>
              )}
            </div>
          )}

          {outStockProduct && (
            <div className={styles['root__btn-group']}>
              <SimilarProductsTrigger productId={data.id} />

              <DeleteProduct
                onClick={async () => {
                  if (isAuthorized) {
                    try {
                      await fetchDropCartItem({
                        variables: {
                          shopId: currentShopId as string,
                          productId: data.id,
                        },
                      })

                      removeProduct(data.id)
                    }
                    catch (error) {
                      console.error('fetchDropCartItem error: ', error)
                      setIsDeleted(false)
                      openErrorInfoModal({
                        description: te('drop_cart_item'),
                      })
                    }
                  }
                  else {
                    removeProduct(data.id)
                  }
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Mobile bottom actions row */}
      {isMobile && !outStockProduct && (
        <div className={styles.root__mobileActions}>
          {isDeleted ? (
            <ButtonDelay
              className={styles.root__control}
              duration={3000}
              onFinish={async () => {
                await handleDelete()
              }}
              onClick={() => {
                setIsDeleted(false)
              }}
            >
              {tca('preview.cart_return')}
            </ButtonDelay>
          ) : (
            <>
              <ActionButtons product={data} onRemove={() => setIsDeleted(true)} size='medium' buttonsTemplate={[ 'comment', 'favorite', 'delete' ]} />
              <ControlCart
                classNameControl={styles.root__controlMobile}
                isTablet={true}
                product={data}
                noTotals={true}
              />
            </>
          )}
        </div>
      )}
    </div>
  )
}

function areEqual (
  prev: Readonly<ProductCartCardProps>,
  next: Readonly<ProductCartCardProps>,
) {
  return (
    prev.amount === next.amount &&
    prev.className === next.className &&
    prev.data.id === next.data.id &&
    prev.data.price === next.data.price &&
    prev.data.oldPrice === next.data.oldPrice
  )
}

export default memo(ProductCart, areEqual)
